import {WorkOutcome} from 'notebook/workers/core/types';

// ContentWorkers get imported via a path which results in errors around uninitialized state when we try to
// import from BenchlingProfiler. To avoid this, we import logContentWork asynchronously. In principle, we
// could drop some profiling events as a consequence of this, but that's both innocuous and unlikely to
// happen in practice.
let _logWork: (name: string) => (outcome: WorkOutcome) => void = () => () => {};

import('notebook/workers/core/logWork').then((module) => {
  _logWork = module.default;
});

/**
 * An object with callbacks to interact with a content worker while recording performance data across
 * various different outcomes.
 * @template U The type of the result of the content worker.
 */
interface ObservableWork<U> {
  /**
   * Handle the case where content workers are disabled. This will run the fallback, timing its execution
   * and logging the outcome.
   * @returns A promise that resolves to the result of the content worker when it is disabled.
   */
  disabled: () => Promise<U>;
  /**
   * Dispatch work to the content worker. This will initiate work on a content worker, recording the time
   * it takes to complete dispatching. Callers should additionally invoke the succes` callback when the
   * content worker completes successfully, to distinguish this from timeout and error cases.
   * @returns A promise that resolves to the result of the content worker when it is dispatched.
   */
  dispatched: () => Promise<U>;
  /**
   * Handle the case where the content worker encounters an error. This will run the fallback, timing its
   * execution and logging the outcome.
   * @returns A promise that resolves to the result of the content worker when it encounters an error.
   */
  error: () => Promise<U>;
  /**
   * Handle the case where the content worker completes successfully. This will log the outcome of the
   * content worker, and record the total time since dispatching.
   */
  success: () => void;
  /**
   * Handle the case where the content worker times out. This will run the fallback, timing its execution
   * and logging the outcome.
   * @returns A promise that resolves to the result of the content worker when it times out.
   */
  timeout: () => Promise<U>;
}

/**
 * Observe the performance of a content worker. This function provides a set of callbacks which can be
 * invoked to interact with the content worker while recording performance data across various different
 * outcomes.
 *
 * @param name The name of the content worker (for performance logging purposes).
 * @param work A function to execute work on the content worker.
 * @param fallback A function to execute work if the content worker is disabled.
 * @param object The object to pass to the content worker.
 * @returns An object with callbacks to interact with the content worker.
 * @template T The type of the object to pass to the content worker.
 * @template U The type of the result of the content worker.
 */
export default function observe<T, U>(
  name: string,
  work: (object: T) => Promise<U>,
  fallback: (object: T) => U,
  object: T
): ObservableWork<U> {
  const logContentWorkOutcome = _logWork(name);
  const logDispatchingOutcome = _logWork(name);
  const dispatched = (): Promise<U> => {
    const promise = work(object);
    logDispatchingOutcome(WorkOutcome.DISPATCHED);
    return promise;
  };
  const success = (): void => {
    logContentWorkOutcome(WorkOutcome.SUCCESS);
  };
  const logsFallbackOutcome =
    (outcome: WorkOutcome): (() => Promise<U>) =>
    () => {
      const result = fallback(object);
      logContentWorkOutcome(outcome);
      return Promise.resolve(result);
    };

  return {
    disabled: logsFallbackOutcome(WorkOutcome.DISABLED),
    dispatched,
    error: logsFallbackOutcome(WorkOutcome.ERROR),
    success,
    timeout: logsFallbackOutcome(WorkOutcome.TIMEOUT),
  };
}
